import {
  changedFacts as changedRows,
  materialize as materializeRows,
  type Citation,
  type Observation,
  type Recorded,
  type RecordShape,
} from '@asvise/facts'
import {
  emptyHouseholdFacts,
  emptyPersonFacts,
  HOUSEHOLD_FACT_FIELDS,
  HOUSEHOLD_FACT_REGISTRY,
  PERSON_FACT_FIELDS,
  PERSON_FACT_REGISTRY,
  type Answers,
  type HouseholdFactField,
  type HouseholdFacts,
  type Person,
  type PersonFactField,
  type PersonFacts,
  type PersonId,
  type PersonRole,
} from './vocabulary.ts'

/**
 * The live working state of a household, as rows rather than as a document.
 *
 * The arithmetic is `@asvise/facts`: one row per observation, appended and never
 * revised, resolved to one fact per field by rank with recency breaking ties.
 * The commercial engine's version of this file says a household record would
 * want every line of it, and that turned out to be true without a change to the
 * core: `materialize` is generic over the field union and the answers shape, and
 * `changedFacts` spreads whatever columns identify the subject without ever
 * naming one.
 *
 * What it could not know is that there are two subjects. A household's savings
 * and a person's age are both facts, they expire on different schedules, and
 * they are keyed differently. So this file calls the same generic machinery
 * twice with two shapes, which is the whole of the adaptation.
 */
const HOUSEHOLD_SHAPE: RecordShape<HouseholdFactField, HouseholdFacts> = {
  fields: HOUSEHOLD_FACT_FIELDS,
  empty: emptyHouseholdFacts,
  staleness: (field) => HOUSEHOLD_FACT_REGISTRY[field].staleness_months,
}

const PERSON_SHAPE: RecordShape<PersonFactField, PersonFacts> = {
  fields: PERSON_FACT_FIELDS,
  empty: emptyPersonFacts,
  staleness: (field) => PERSON_FACT_REGISTRY[field].staleness_months,
}

/** What identifies a household fact row. */
export interface HouseholdFactOwner {
  household_id: string
  /**
   * Who made this observation.
   *
   * Not an access control column: the table is reached through the household.
   * It is provenance, and it matters more here than it did commercially,
   * because two adults answering one record is the ordinary case rather than
   * the exception.
   */
  user_id: string
}

/** What identifies a person fact row: the household, and which member. */
export interface PersonFactOwner extends HouseholdFactOwner {
  person_id: PersonId
}

export interface HouseholdFactInsert
  extends Observation<HouseholdFactField>,
    HouseholdFactOwner {}
export interface PersonFactInsert extends Observation<PersonFactField>, PersonFactOwner {}

export type HouseholdFactRow = Recorded<HouseholdFactInsert>
export type PersonFactRow = Recorded<PersonFactInsert>

/** A member as the database holds them: identity, with no facts attached yet. */
export interface PersonRow {
  id: PersonId
  household_id: string
  label: string
  role: PersonRole
  position: number
}

export function materializeHousehold(
  rows: HouseholdFactRow[],
  opts: { now?: string } = {},
): HouseholdFacts {
  return materializeRows(rows, HOUSEHOLD_SHAPE, opts)
}

/**
 * One member's facts, from the rows that are about them.
 *
 * Filtering is done here rather than left to the caller, because a caller that
 * forgot would silently resolve one person's answers from another's rows and
 * the result would look perfectly ordinary.
 */
export function materializePerson(
  person_id: PersonId,
  rows: PersonFactRow[],
  opts: { now?: string } = {},
): PersonFacts {
  return materializeRows(
    rows.filter((r) => r.person_id === person_id),
    PERSON_SHAPE,
    opts,
  )
}

/**
 * The whole record, assembled from what the database holds.
 *
 * The one place that knows a household is a household and a person is a person.
 * Everything above it is generic and everything below it is a query.
 */
export function assembleAnswers(input: {
  household_name: string
  people: PersonRow[]
  household_rows: HouseholdFactRow[]
  person_rows: PersonFactRow[]
  policies: Answers['policies']
  now?: string
}): Answers {
  const opts = input.now === undefined ? {} : { now: input.now }
  const people: Person[] = [...input.people]
    // Ordered here rather than relied on from the query, so a record reads the
    // same whichever way it was fetched.
    .sort((a, b) => a.position - b.position)
    .map((p) => ({
      id: p.id,
      label: p.label,
      role: p.role,
      facts: materializePerson(p.id, input.person_rows, opts),
    }))

  return {
    household_name: input.household_name,
    people,
    policies: input.policies,
    ...materializeHousehold(input.household_rows, opts),
  }
}

/** The household rows to append for a change. */
export function changedHouseholdFacts(
  previous: HouseholdFacts | null | undefined,
  current: HouseholdFacts,
  ctx: HouseholdFactOwner,
): HouseholdFactInsert[] {
  return changedRows(previous, current, ctx, HOUSEHOLD_SHAPE)
}

/** The rows to append for one member, which is why the person is in the context. */
export function changedPersonFacts(
  previous: PersonFacts | null | undefined,
  current: PersonFacts,
  ctx: PersonFactOwner,
): PersonFactInsert[] {
  return changedRows(previous, current, ctx, PERSON_SHAPE)
}

/**
 * Every row a change to the whole record would append.
 *
 * A member who is new to the record has no previous facts, which is exactly what
 * `changedFacts` treats as "everything is new" without being told. A member who
 * has left produces nothing: their rows stay in the table, addressed to a person
 * the record no longer lists, which is the same way a retired question's answers
 * stay and take no part.
 */
export function changedRecord(
  previous: Answers | null | undefined,
  current: Answers,
  ctx: HouseholdFactOwner,
): { household: HouseholdFactInsert[]; person: PersonFactInsert[] } {
  const before = new Map(previous?.people.map((p) => [p.id, p.facts]) ?? [])

  return {
    household: changedHouseholdFacts(previous ?? null, current, ctx),
    person: current.people.flatMap((p) =>
      changedPersonFacts(before.get(p.id) ?? null, p.facts, { ...ctx, person_id: p.id }),
    ),
  }
}

/**
 * One place where a person's answer replaced what a document said.
 *
 * Framework section 10.3 calls these the user corrections, and they are the
 * most interesting line of an audit record. A correction is not a mistake: a
 * schedule can be out of date, a household can hold an endorsement a reader
 * never saw, and the fact model is built so that a person outranks a document
 * precisely because that is usually right. What matters is that an assessment
 * resting on a corrected figure says so, because the next reader of the record
 * will otherwise take the figure for the document's.
 */
export interface Correction {
  field: string
  /** The member this is about, where it is about one. */
  person_id?: PersonId
  /** What the person said, and when they said it. */
  said: unknown
  said_at: string
  /** What the document said, and where it said it. */
  instead_of: unknown
  document_observed_at: string
  citation?: Citation | null
}

function correctionsIn<F extends string>(
  rows: Recorded<Observation<F> & { person_id?: PersonId }>[],
): Correction[] {
  /** Grouped by what the rows are accounts of: one field, for one member. */
  const byField = new Map<string, typeof rows>()
  for (const row of rows) {
    const key = `${row.person_id ?? ''}:${row.field}`
    const held = byField.get(key)
    if (held) held.push(row)
    else byField.set(key, [row])
  }

  const out: Correction[] = []
  for (const group of byField.values()) {
    /**
     * The newest of each, which is the only pair worth reporting.
     *
     * A field answered three times by a person and twice by a document is one
     * correction, not six. What a correction is about is the standing
     * disagreement between the two accounts, and the standing account of each
     * is its most recent.
     */
    const user = newest(group.filter((r) => r.source === 'USER'))
    const document = newest(group.filter((r) => r.source === 'DOCUMENT'))
    if (!user || !document) continue

    /**
     * A person who typed what the document already said has corrected nothing.
     * Compared as JSON because a value is `unknown` here: this layer carries
     * whatever a field holds and has never needed to know which type it is.
     */
    if (JSON.stringify(user.value) === JSON.stringify(document.value)) continue

    /**
     * And a person whose answer predates the document has not overridden it.
     * The document is the later account and `preferFact` is what decides which
     * wins; reporting this as a correction would describe an override that did
     * not happen.
     */
    if (user.observed_at < document.observed_at) continue

    out.push({
      field: user.field,
      ...(user.person_id ? { person_id: user.person_id } : {}),
      said: user.value,
      said_at: user.observed_at,
      instead_of: document.value,
      document_observed_at: document.observed_at,
      ...(document.citation ? { citation: document.citation } : {}),
    })
  }

  return out
}

function newest<R extends { observed_at: string }>(rows: R[]): R | undefined {
  return rows.reduce<R | undefined>(
    (best, row) => (best === undefined || row.observed_at > best.observed_at ? row : best),
    undefined,
  )
}

/** Every correction on the record, household facts and person facts together. */
export function corrections(
  household_rows: HouseholdFactRow[],
  person_rows: PersonFactRow[],
): Correction[] {
  return [...correctionsIn(household_rows), ...correctionsIn(person_rows)]
}
